import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { useEmailStore } from './emailStore';
import type { Email } from '@/types/email';

const MAX_UNDO_STACK_SIZE = 20;

// The currently-executing undo/redo, so a second toast's Undo click can wait
// behind it instead of failing (N26).
let inFlightUndoRedo: Promise<boolean> | null = null;

export type UndoableActionType = 'archive' | 'delete' | 'move' | 'custom';

/**
 * Self-contained undo/redo handlers for actions that aren't email moves
 * (e.g. briefing item dismissal). Entries carrying these bypass the email
 * action engine entirely; everything else (stacks, toasts, Cmd+Z) is shared.
 */
export interface CustomUndoHandlers {
  undo: () => Promise<boolean>;
  redo: () => Promise<boolean>;
}

/** Strip heavy fields from emails to reduce memory in undo stack */
export function stripEmailBody(email: Email): Email {
  const { body, bodyHtml, headers, ocrText, ocrMetadata, spamReport, calendarEvent, ...rest } = email;
  void body; void bodyHtml; void headers; void ocrText; void ocrMetadata; void spamReport; void calendarEvent;
  return { ...rest, body: '', bodyHtml: null, headers: undefined, ocrText: undefined, ocrMetadata: undefined, spamReport: undefined, calendarEvent: undefined };
}

/**
 * One undoable unit per affected message. A cross-account bulk action sends
 * each email to ITS OWN account's destination, so undo must move each back
 * FROM its own destination — never a single shared folder pair.
 */
export interface UndoItem {
  emailId: string;
  accountId: string;
  /** Original folder — undo moves the message back TO here. */
  fromFolderId: string;
  /** Action destination — undo moves the message FROM here. */
  toFolderId: string;
  /**
   * Stripped snapshot for optimistic restore. Null for messages the client
   * never loaded (sender bulk actions report the complete affected id set);
   * those are still moved back server-side, they just can't be optimistically
   * re-rendered before the reload/echo brings them in.
   */
  email: Email | null;
}

export interface UndoableAction {
  id: string;
  type: UndoableActionType;
  items: UndoItem[];
  description: string;
  /** Sender suppression mark to clear once the undo move resolves. */
  senderEmail: string | null;
  previousSelectedEmailId: string | null;
  custom?: CustomUndoHandlers;
  /**
   * Optional side-effect handlers that ride along with an ENGINE entry
   * (compound actions like briefing "Archive & done"): after the engine
   * successfully undoes/redoes the email move, the companion reverses/reapplies
   * the non-email half. Companion failure is non-fatal — the emails are already
   * moved, so the entry still resolves rather than allowing a double-undo.
   */
  companion?: CustomUndoHandlers;
}

interface UndoStoreState {
  undoStack: UndoableAction[];
  redoStack: UndoableAction[];
  isProcessing: boolean;
  /** Increments on each successful undo/redo - use to trigger UI refreshes */
  undoRedoVersion: number;

  /** Records an entry and returns its id (bind undo toasts to THIS id). */
  recordAction: (
    action: Omit<UndoableAction, 'id' | 'previousSelectedEmailId' | 'senderEmail'> & {
      senderEmail?: string | null;
      /**
       * Selection captured BEFORE the optimistic removal (N26): entries are
       * recorded after dispatch, by which time the auto-advance has already
       * moved selection to the neighbor — restoring THAT was a dead feature.
       */
      previousSelectedEmailId?: string | null;
    }
  ) => string;
  /** Drops an entry (e.g. the action it belongs to failed). */
  removeAction: (entryId: string) => void;
  /**
   * Undoes a SPECIFIC entry when an id is given (undo toasts), or the most
   * recent one (Cmd+Z). The entry is removed from the stack wherever it sits —
   * two stacked toasts each undo their own action.
   */
  undo: (entryId?: string) => Promise<boolean>;
  redo: () => Promise<boolean>;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearAll: () => void;
}

export const useUndoStore = create<UndoStoreState>()(
  conditionalDevtools(
    (set, get) => ({
      undoStack: [],
      redoStack: [],
      isProcessing: false,
      undoRedoVersion: 0,

      recordAction: (action) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        // Prefer the caller's pre-removal capture (N26); the fallback read here
        // happens AFTER auto-advance for engine-recorded entries.
        const previousSelectedEmailId =
          action.previousSelectedEmailId !== undefined
            ? action.previousSelectedEmailId
            : useEmailStore.getState().selectedEmailId;
        const newAction: UndoableAction = {
          ...action,
          id,
          senderEmail: action.senderEmail ?? null,
          previousSelectedEmailId,
          items: action.items.map((item) => ({
            ...item,
            email: item.email ? stripEmailBody(item.email) : null,
          })),
        };

        set((state) => {
          const newStack = [newAction, ...state.undoStack].slice(0, MAX_UNDO_STACK_SIZE);
          return {
            undoStack: newStack,
            redoStack: [], // Clear redo stack when new action is recorded
          };
        });
        return id;
      },

      removeAction: (entryId) => {
        set((state) => {
          if (!state.undoStack.some((entry) => entry.id === entryId)) {
            return {};
          }
          return { undoStack: state.undoStack.filter((entry) => entry.id !== entryId) };
        });
      },

      undo: async (entryId) => {
        // Another undo mid-flight (N26): wait for it instead of reporting a
        // false "Failed to undo" to the second toast's click.
        while (get().isProcessing && inFlightUndoRedo) {
          await inFlightUndoRedo.catch(() => undefined);
        }
        const state = get();
        if (state.isProcessing || state.undoStack.length === 0) {
          return false;
        }
        const action = entryId
          ? state.undoStack.find((entry) => entry.id === entryId)
          : state.undoStack[0];
        if (!action) {
          return false;
        }

        set({ isProcessing: true });
        // Execution lives in the action engine: per-id serialization, optimistic
        // restore, suppression-mark clearing after the move resolves, loud
        // failure handling.
        const run = (async () => {
          try {
            let success: boolean;
            if (action.custom) {
              success = await action.custom.undo();
            } else {
              const { performUndo } = await import('./emailCommands');
              success = await performUndo(action);
            }
            if (success && action.companion) {
              try {
                const companionOk = await action.companion.undo();
                if (!companionOk) {
                  console.error('Companion undo failed for entry', action.id);
                }
              } catch (companionError) {
                console.error('Companion undo threw for entry', action.id, companionError);
              }
            }
            if (success) {
              set((current) => ({
                undoStack: current.undoStack.filter((entry) => entry.id !== action.id),
                redoStack: [action, ...current.redoStack].slice(0, MAX_UNDO_STACK_SIZE),
                isProcessing: false,
                undoRedoVersion: current.undoRedoVersion + 1,
              }));
              return true;
            }
            set({ isProcessing: false });
            return false;
          } catch (error) {
            console.error('Failed to undo action:', error);
            set({ isProcessing: false });
            return false;
          }
        })();
        inFlightUndoRedo = run;
        try {
          return await run;
        } finally {
          if (inFlightUndoRedo === run) {
            inFlightUndoRedo = null;
          }
        }
      },

      redo: async () => {
        const state = get();
        if (state.isProcessing || state.redoStack.length === 0) {
          return false;
        }

        const action = state.redoStack[0];
        set({ isProcessing: true });
        try {
          let success: boolean;
          if (action.custom) {
            success = await action.custom.redo();
          } else {
            const { performRedo } = await import('./emailCommands');
            success = await performRedo(action);
          }
          if (success && action.companion) {
            try {
              const companionOk = await action.companion.redo();
              if (!companionOk) {
                console.error('Companion redo failed for entry', action.id);
              }
            } catch (companionError) {
              console.error('Companion redo threw for entry', action.id, companionError);
            }
          }
          if (success) {
            set((current) => ({
              redoStack: current.redoStack.slice(1),
              undoStack: [action, ...current.undoStack].slice(0, MAX_UNDO_STACK_SIZE),
              isProcessing: false,
              undoRedoVersion: current.undoRedoVersion + 1,
            }));
            return true;
          }
          set({ isProcessing: false });
          return false;
        } catch (error) {
          console.error('Failed to redo action:', error);
          set({ isProcessing: false });
          return false;
        }
      },

      canUndo: () => {
        const state = get();
        return !state.isProcessing && state.undoStack.length > 0;
      },

      canRedo: () => {
        const state = get();
        return !state.isProcessing && state.redoStack.length > 0;
      },

      clearAll: () => {
        set({ undoStack: [], redoStack: [] });
      },
    }),
    { name: 'UndoStore' }
  )
);
